import { useEffect, useState } from 'react'
import { Loader2, Building2, Trophy, Users, Gift, Save } from 'lucide-react'
import {
  getSponsorDashboard,
  getSponsorProfile,
  updateSponsorProfile,
  SponsorDashboardResponse,
  SponsorProfile,
} from '../api'

const STATUS_LABELS: Record<string, string> = {
  draft: 'Draft',
  review: 'In review',
  published: 'Published',
  archived: 'Archived',
}

const STATUS_COLORS: Record<string, string> = {
  draft: 'bg-stone-100 text-stone-700',
  review: 'bg-amber-100 text-amber-800',
  published: 'bg-green-100 text-green-800',
  archived: 'bg-stone-200 text-stone-600',
}

function StatTile({ icon: Icon, label, value }: { icon: typeof Trophy; label: string; value: number }) {
  return (
    <div className="rounded-xl border border-stone-200 bg-white p-5 shadow-sm">
      <div className="flex items-center gap-3">
        <div className="flex h-10 w-10 items-center justify-center rounded-lg bg-amber-100 text-amber-700">
          <Icon className="h-5 w-5" />
        </div>
        <div>
          <p className="text-xs uppercase tracking-wide text-stone-500">{label}</p>
          <p className="text-2xl font-bold text-stone-900">{value.toLocaleString()}</p>
        </div>
      </div>
    </div>
  )
}

function ProfileEditor() {
  const [profile, setProfile] = useState<SponsorProfile | null>(null)
  const [loading, setLoading] = useState(true)
  const [saving, setSaving] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [savedAt, setSavedAt] = useState<number | null>(null)

  useEffect(() => {
    let cancelled = false
    getSponsorProfile()
      .then((p) => {
        if (!cancelled) setProfile(p)
      })
      .catch((err) => {
        if (!cancelled)
          setError(err instanceof Error ? err.message : 'Failed to load profile')
      })
      .finally(() => {
        if (!cancelled) setLoading(false)
      })
    return () => {
      cancelled = true
    }
  }, [])

  async function save(e: React.FormEvent) {
    e.preventDefault()
    if (!profile) return
    setSaving(true)
    setError(null)
    try {
      const updated = await updateSponsorProfile({
        business_name: profile.business_name ?? null,
        business_logo_url: profile.business_logo_url ?? null,
        business_website: profile.business_website ?? null,
        business_description: profile.business_description ?? null,
      })
      setProfile(updated)
      setSavedAt(Date.now())
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to save profile')
    } finally {
      setSaving(false)
    }
  }

  if (loading) {
    return (
      <div className="rounded-xl border border-stone-200 bg-white p-6 shadow-sm">
        <Loader2 className="h-5 w-5 animate-spin text-[#f59e0b]" />
      </div>
    )
  }

  if (!profile) return null

  return (
    <form
      onSubmit={save}
      className="rounded-xl border border-stone-200 bg-white p-6 shadow-sm"
    >
      <div className="mb-4 flex items-center gap-2">
        <Building2 className="h-5 w-5 text-amber-700" />
        <h2 className="text-lg font-semibold text-stone-900">Business profile</h2>
      </div>
      <p className="mb-4 text-sm text-stone-500">
        This information appears on hunts you sponsor.
      </p>

      <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
        <div>
          <label className="mb-1 block text-xs font-medium uppercase tracking-wide text-stone-600">
            Business name
          </label>
          <input
            type="text"
            value={profile.business_name ?? ''}
            onChange={(e) => setProfile({ ...profile, business_name: e.target.value })}
            className="w-full rounded-lg border border-stone-300 px-3 py-2 text-sm focus:border-amber-400 focus:outline-none focus:ring-2 focus:ring-amber-100"
            placeholder="Acme Co."
          />
        </div>
        <div>
          <label className="mb-1 block text-xs font-medium uppercase tracking-wide text-stone-600">
            Website
          </label>
          <input
            type="url"
            value={profile.business_website ?? ''}
            onChange={(e) => setProfile({ ...profile, business_website: e.target.value })}
            className="w-full rounded-lg border border-stone-300 px-3 py-2 text-sm focus:border-amber-400 focus:outline-none focus:ring-2 focus:ring-amber-100"
            placeholder="https://example.com"
          />
        </div>
        <div className="sm:col-span-2">
          <label className="mb-1 block text-xs font-medium uppercase tracking-wide text-stone-600">
            Logo URL
          </label>
          <input
            type="url"
            value={profile.business_logo_url ?? ''}
            onChange={(e) => setProfile({ ...profile, business_logo_url: e.target.value })}
            className="w-full rounded-lg border border-stone-300 px-3 py-2 text-sm focus:border-amber-400 focus:outline-none focus:ring-2 focus:ring-amber-100"
            placeholder="https://cdn.example.com/logo.png"
          />
        </div>
        <div className="sm:col-span-2">
          <label className="mb-1 block text-xs font-medium uppercase tracking-wide text-stone-600">
            Description
          </label>
          <textarea
            value={profile.business_description ?? ''}
            onChange={(e) =>
              setProfile({ ...profile, business_description: e.target.value })
            }
            rows={3}
            className="w-full rounded-lg border border-stone-300 px-3 py-2 text-sm focus:border-amber-400 focus:outline-none focus:ring-2 focus:ring-amber-100"
            placeholder="Tell hunters who you are."
          />
        </div>
      </div>

      {error && (
        <div className="mt-3 rounded-md border border-red-200 bg-red-50 px-3 py-2 text-xs text-red-700">
          {error}
        </div>
      )}

      <div className="mt-4 flex items-center justify-between">
        <p className="text-xs text-stone-400">
          {savedAt ? 'Saved' : ''}
        </p>
        <button
          type="submit"
          disabled={saving}
          className="inline-flex items-center gap-2 rounded-lg bg-amber-500 px-4 py-2 text-sm font-medium text-white shadow-sm transition-colors hover:bg-amber-600 disabled:opacity-60"
        >
          {saving ? (
            <Loader2 className="h-4 w-4 animate-spin" />
          ) : (
            <Save className="h-4 w-4" />
          )}
          Save
        </button>
      </div>
    </form>
  )
}

export default function SponsorDashboardPage() {
  const [data, setData] = useState<SponsorDashboardResponse | null>(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)

  useEffect(() => {
    let cancelled = false
    setLoading(true)
    getSponsorDashboard()
      .then((d) => {
        if (!cancelled) setData(d)
      })
      .catch((err) => {
        if (!cancelled)
          setError(err instanceof Error ? err.message : 'Failed to load dashboard')
      })
      .finally(() => {
        if (!cancelled) setLoading(false)
      })
    return () => {
      cancelled = true
    }
  }, [])

  if (loading) {
    return (
      <div className="flex h-full items-center justify-center">
        <Loader2 className="h-8 w-8 animate-spin text-[#f59e0b]" />
      </div>
    )
  }

  if (error) {
    return (
      <div className="p-8">
        <div className="rounded-lg border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-700">
          {error}
        </div>
      </div>
    )
  }

  if (!data) return null

  return (
    <div className="p-8">
      <div className="mb-8">
        <h1 className="text-2xl font-bold text-stone-900">Sponsor dashboard</h1>
        <p className="mt-1 text-sm text-stone-500">
          Hunts you're sponsoring and how they're performing.
        </p>
      </div>

      <div className="mb-8 grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-4">
        <StatTile icon={Building2} label="Sponsored hunts" value={data.totals.hunts} />
        <StatTile icon={Users} label="Total starts" value={data.totals.starts} />
        <StatTile icon={Trophy} label="Total completions" value={data.totals.completions} />
        <StatTile icon={Gift} label="Rewards claimed" value={data.totals.rewards_claimed} />
      </div>

      <div className="mb-8">
        <ProfileEditor />
      </div>

      {data.hunts.length === 0 ? (
        <div className="flex flex-col items-center justify-center rounded-xl border border-dashed border-stone-300 py-16">
          <Building2 className="mb-3 h-12 w-12 text-stone-300" />
          <p className="text-stone-500">You're not sponsoring any hunts yet.</p>
          <p className="mt-1 text-xs text-stone-400">
            An admin can attach you as a sponsor in the hunt editor.
          </p>
        </div>
      ) : (
        <div className="overflow-hidden rounded-xl border border-stone-200 bg-white shadow-sm">
          <table className="w-full">
            <thead>
              <tr className="border-b border-stone-200 bg-stone-50">
                <th className="px-6 py-3 text-left text-xs font-medium uppercase tracking-wider text-stone-500">Hunt</th>
                <th className="px-6 py-3 text-left text-xs font-medium uppercase tracking-wider text-stone-500">Status</th>
                <th className="px-6 py-3 text-left text-xs font-medium uppercase tracking-wider text-stone-500">Starts</th>
                <th className="px-6 py-3 text-left text-xs font-medium uppercase tracking-wider text-stone-500">Completions</th>
                <th className="px-6 py-3 text-left text-xs font-medium uppercase tracking-wider text-stone-500">Reward pool</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-stone-100">
              {data.hunts.map((h) => {
                const fr = h.featured_reward
                const isPool = fr && fr.total_quantity != null
                const exhausted = isPool && (fr!.remaining ?? 0) <= 0
                return (
                  <tr key={h.id} className="transition-colors hover:bg-stone-50">
                    <td className="px-6 py-4">
                      <p className="font-medium text-stone-900">{h.title}</p>
                    </td>
                    <td className="px-6 py-4">
                      <span
                        className={`inline-flex rounded-full px-2.5 py-0.5 text-xs font-medium ${
                          STATUS_COLORS[h.status] ?? 'bg-stone-100 text-stone-700'
                        }`}
                      >
                        {STATUS_LABELS[h.status] ?? h.status}
                      </span>
                    </td>
                    <td className="px-6 py-4 text-sm text-stone-700">{h.total_starts}</td>
                    <td className="px-6 py-4 text-sm text-stone-700">{h.total_completions}</td>
                    <td className="px-6 py-4">
                      {!fr ? (
                        <span className="text-xs text-stone-400">No reward</span>
                      ) : isPool ? (
                        <span
                          className={`text-xs font-medium ${
                            exhausted ? 'text-stone-500' : 'text-amber-800'
                          }`}
                        >
                          {fr.claimed_quantity}/{fr.total_quantity} claimed
                          {exhausted ? ' · exhausted' : ''}
                        </span>
                      ) : (
                        <span className="text-xs text-stone-600">
                          {fr.claimed_quantity} claimed (unlimited)
                        </span>
                      )}
                    </td>
                  </tr>
                )
              })}
            </tbody>
          </table>
        </div>
      )}
    </div>
  )
}
